import React, { useMemo } from 'react';
import { ListBase, ListProps, useListContext } from 'react-admin';
import { FilterField } from '../Common/DataList';
import { NetOrcaTableListV1, TableListColumn } from '../Common/NetOrcaTableList';
import SubmissionRow from './List/SubmissionRow';
import './Submissions.css';

const SubmissionsListView = () => {
    const { filterValues } = useListContext();

    /**
     * Everything the API's SubmissionFilterSet accepts that is worth asking
     * for. Each one has a column of its own, so they all sit on the header and
     * the list needs no filter panel at all.
     */
    const filterFields: FilterField[] = useMemo(
        () => [
            {
                // the number someone quotes when they are after one submission
                name: 'id',
                label: 'ID',
                type: 'search',
                placeholder: 'Search by ID',
                dataCy: 'id-search',
            },
            {
                name: 'commit_id',
                label: 'Commit',
                type: 'search',
                placeholder: 'Search by commit',
                dataCy: 'commit-search',
            },
            {
                // the API takes the two ends separately
                name: 'created',
                label: 'Created',
                type: 'dateRange',
                startName: 'created_after',
                endName: 'created_before',
                dataCy: 'created-filter',
            },
        ],
        [],
    );

    const columns: TableListColumn[] = useMemo(
        () => [
            { label: 'ID', filters: ['id'], dataCy: 'column-id' },
            { label: 'Commit', filters: ['commit_id'], dataCy: 'column-commit' },
            { label: 'State', dataCy: 'column-state' },
            { label: 'Changes', align: 'right' as const, dataCy: 'column-changes' },
            { label: 'Created', filters: ['created'], dataCy: 'column-created' },
            // the last track is the chevron's, which the header cannot name
        ],
        [],
    );

    return (
        <NetOrcaTableListV1
            className="netorca-sub"
            dataCy="submission-result-list"
            fields={filterFields}
            columns={columns}
            template="var(--sub-cols)"
            emptyText="No submissions found"
            noun="submission"
            drawerTitle="Filter submissions"
            renderRow={(record) => <SubmissionRow key={record.id} record={record} />}
        />
    );
};

/**
 * The submissions a team has made. Each row opens the submission it stands
 * for; the change count opens the changes that submission raised.
 */
const SubmissionsList = (props: ListProps) => (
    <ListBase
        // the light serializer carries everything the rows show, count included
        filter={{ light: true }}
        sort={{ field: 'id', order: 'DESC' }}
        // page size is the batch the infinite scroll fetches
        perPage={25}
        {...props}
    >
        <SubmissionsListView />
    </ListBase>
);

export default SubmissionsList;
